/*
 * Copyright (c) 2023. 듣고 배우는 한국어 프로젝트.
 * by jhpark
 */

import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {Subscription} from "rxjs";

import {SimpleDialect} from "../../models/dialect.model";
import {LinkInfo} from "../../defines/client.const";
import {GetRegion2Css, GetRegion2Name} from '../../defines/desktop.const';
import {Word} from "../../models/word.model";
import {GlobalEventService} from "../../services/global-event/global-event.service";
import {VoiceService} from "../../services/voice/voice.service";
import {clickOutsideLinkMixin} from "../../mixin/click-outside.mixin";
import {TotalLinkInfo, TotalLinkParam, TotalService} from "../../services/total/total.service";
import {
  LinkPopupPlacement,
  LinkPopupPlacementStyle,
  resolveLinkPopupAutoPlacement,
} from './link-popup-placement';

const MAX_POPUP_ITEM = 3;

@Component({
  selector: 'app-link-popup',
  templateUrl: './link-popup.component.html',
  styleUrls: ['./link-popup.component.scss'],
})
export class LinkPopupComponent extends clickOutsideLinkMixin implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('style.position') stylePosition = 'absolute';
  @HostBinding('style.top') styleTop = "0";
  @HostBinding('style.left') styleLeft = "0";
  @HostBinding('style.visibility') styleVisibility = "visible";
  @Input() linkInfo?: LinkInfo;
  @Input() placement: LinkPopupPlacement = 'bottom';
  @Output() close = new EventEmitter<LinkInfo | null>();
  @Output() viewInit = new EventEmitter<HTMLElement>();

  protected readonly GetRegion2Css = GetRegion2Css;
  protected readonly GetRegion2Name = GetRegion2Name;

  private load$: Subscription | null = null;

  protected get IsLoading(): boolean {
    return this.load$ !== null;
  }

  private words?: Array<Word>;
  private dialects?: Array<SimpleDialect>;
  private placementRafId?: number;
  private placementRetryTimeoutId?: ReturnType<typeof setTimeout>;
  private placementQueued = false;
  private placementRetries = 0;
  private destroyed = false;
  private static readonly MAX_PLACEMENT_RETRIES = 5;

  /**
   * 팝업창에 표시할 단어 목록 (최대 MAX_POPUP_ITEM 개)
   * @protected
   */
  protected get listWord(): Array<Word> {
    return (this.words ?? []).slice(0, MAX_POPUP_ITEM);
  }

  /**
   * 팝업창에 표시할 지방어 목록 (단어 포함, 최대 MAX_POPUP_ITEM 개)
   * @protected
   */
  protected get listDialect(): Array<SimpleDialect> {
    const lenWord = this.words?.length ?? 0
    const lenDialect = Math.max(0, Math.min((this.dialects?.length ?? 0), MAX_POPUP_ITEM - lenWord));
    return (this.dialects ?? []).slice(0, lenDialect);
  }

  /**
   * 팝업창에 표시되지 않은 단어의 갯수
   * @private
   */
  private get leftWordCount(): number {
    return Math.max((this.words?.length ?? 0) - MAX_POPUP_ITEM, 0);
  }

  /**
   * 팝업창에 표시되지 않은 지방어의 갯수
   * @private
   */
  private get leftDialectCount(): number {
    return (this.dialects?.length ?? 0) - this.listDialect.length - this.listWord.length;
  }

  protected getMoreTxt(): string | null {
    let moreTxt: string | null = null;
    if (this.leftWordCount > 0 && this.leftDialectCount > 0) {
      moreTxt = `${this.leftWordCount}개의 단어, ${this.leftDialectCount}개의 지방어가 있습니다.`;
    }
    else if (this.leftWordCount > 0) {
      moreTxt = `${this.leftWordCount}개의 단어가 더 있습니다.`;
    }
    else if (this.leftDialectCount > 0) {
      moreTxt = `${this.leftDialectCount}개의 우리말이 더 있습니다.`;
    }
    return moreTxt;
  }


  constructor(
    public override elementRef: ElementRef,
    public override renderer: Renderer2,
    public override globalEventSvc: GlobalEventService,
    private cdRef: ChangeDetectorRef,
    private totalSvc: TotalService,
    private voice: VoiceService,
  ) {
    super(elementRef, renderer, globalEventSvc);
  }

  ngOnInit(): void {
    if (this.placement === 'auto') {
      this.setPopupVisibility('hidden');
    }

    // const parent: HTMLElement = this.elementRef.nativeElement.parentElement;

    // 상위 position: relative (또는 static이 아닌) 인 요소 찾기
    const offsetParent = this.elementRef.nativeElement.offsetParent as HTMLElement;
    if (!offsetParent) {
      throw new Error('LinkPopupComponent.ngOnInit: offsetParent is null');
    }

    this.setPopupTop(offsetParent.offsetHeight);
    // console.warn('LinkPopupComponent.ngOnInit', this.styleTop, parent.offsetHeight, parent);

    // 소리 재생 중단.
    this.voice.Stop();

    if ((this.linkInfo?.wordIds?.length ?? 0) > 0 || (this.linkInfo?.dialectIds?.length ?? 0) > 0) {
      const param: TotalLinkParam = {
        ...(this.linkInfo?.wordIds ? {wordIds: this.linkInfo.wordIds} : {}),
        ...(this.linkInfo?.dialectIds ? {dialectIds: this.linkInfo.dialectIds} : {}),
      }
      this.load$ = this.totalSvc.GetLinkInfo(param)
        .subscribe((linkInfo: TotalLinkInfo) => {
          console.log('LinkPopupComponent.ngOnInit.linkInfo', linkInfo);

          if ((linkInfo.word?.length ?? 0) > 0) {
            this.words = linkInfo.word;
          }

          if ((linkInfo.dialect?.length ?? 0) > 0) {
            this.dialects = linkInfo.dialect;
          }

          this.load$ = null;

          /**
           * 상위 컴포넌트에서 ChangeDetectionStrategy.OnPush을 지정한 경우에 대비해서 추가.
           * link-content 에서는 내부 초기화가 되면 곤란하므로 ChangeDetectionStrategy.OnPush을 지정한다.
           */
          this.cdRef.detectChanges();
          this.queueViewportPlacement();
        });
    }

    // mixin을 이용해서 팝업창 외부를 클릭했는지 체크한다.
    this.coInit(() => this.close.emit(null));
  }

  ngAfterViewInit() {
    this.queueViewportPlacement();
    this.viewInit.emit(this.elementRef.nativeElement.firstChild);
  }

  ngOnDestroy() {
    // 이후 rAF/setTimeout/Promise 콜백이 한 프레임 안에서 합쳐서 발화하더라도
    // detectChanges가 파괴된 뷰에 호출되지 않도록 플래그를 먼저 세운다.
    this.destroyed = true;
    if (this.placementRafId !== undefined) {
      cancelAnimationFrame(this.placementRafId);
      this.placementRafId = undefined;
    }
    if (this.placementRetryTimeoutId !== undefined) {
      clearTimeout(this.placementRetryTimeoutId);
      this.placementRetryTimeoutId = undefined;
    }

    this.coDestroy();

    // 로딩 하고 있으면, 로딩을 중단한다.
    if (this.load$) {
      this.load$.unsubscribe();
      this.load$ = null;
    }

    // 소리 재생 중단.
    this.voice.Stop();
  }

  protected onCloseWord(word_id: string | null ): void {
    if (word_id) {
      const linkInfo: LinkInfo = {
        wordIds: [word_id],
      };
      this.close.emit(linkInfo);
    }
    else {
      this.close.emit(null);
    }
  }

  protected onCloseDialect(dialect_id: string | null): void {
    if (dialect_id) {
      const linkInfo: LinkInfo = {
        dialectIds: [dialect_id],
      };
      this.close.emit(linkInfo);
    }
    else {
      this.close.emit(null);
    }
  }

  protected onCloseMore($event: MouseEvent): void {
    console.warn('LinkPopupComponent.onCloseMore');

    $event.stopPropagation();
    $event.preventDefault();

    this.close.emit(this.linkInfo);
  }


  /**
   * 상위 요소로 이벤트 전파를 막는다.
   * @param event
   */
  onVoid(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.globalEventSvc.Click(event);
  }

  private setPopupTop(top: number): void {
    this.styleTop = `${Math.round(top)}px`;
    // @HostBinding('style.top') 단일 경로로 적용. rAF 콜백에서 호출되더라도
    // detectChanges로 즉시 반영해 imperative DOM write와의 이중 갱신을 피한다.
    this.cdRef.detectChanges();
  }

  private queueViewportPlacement(): void {
    if (this.placement !== 'auto') {
      return;
    }
    if (this.placementQueued) {
      return;
    }
    this.placementQueued = true;

    this.setPopupVisibility('hidden');

    // 더블 rAF: 자식 컴포넌트 렌더링과 레이아웃이 완전히 반영된 뒤
    // getBoundingClientRect로 팝업 높이를 측정하기 위해 한 프레임 더 미룬다.
    // 단일 rAF만으로는 첫 프레임 시점에 popupEl 높이가 0으로 잡히는 사례가 있다.
    this.placementRafId = requestAnimationFrame(() => {
      if (this.destroyed) {
        return;
      }
      this.placementRafId = requestAnimationFrame(() => {
        if (this.destroyed) {
          return;
        }
        this.placementQueued = false;
        this.placementRafId = undefined;
        this.applyViewportPlacement();
      });
    });
  }

  private applyViewportPlacement(): void {
    const hostEl = this.elementRef.nativeElement as HTMLElement;
    const offsetParent = hostEl.offsetParent as HTMLElement | null;
    const popupEl = hostEl.querySelector('article.tooltip-layout1') as HTMLElement | null;

    if (!offsetParent || !popupEl) {
      // 자동 배치 계산에 필요한 요소를 찾지 못한 경우, 기본 아래 배치 그대로 노출한다.
      // 무조건 hidden 상태로 남기지 않도록 visibility만 visible 처리한다.
      console.warn(
        'LinkPopupComponent.applyViewportPlacement: 자동 배치 건너뜀 — 기본 아래 배치 유지',
        {hasOffsetParent: !!offsetParent, hasPopupEl: !!popupEl},
      );
      this.setPopupVisibility('visible');
      return;
    }

    const anchorHeight = offsetParent.offsetHeight;
    this.applyPopupPlacement(popupEl, {
      kind: 'fit',
      top: anchorHeight,
    });

    const anchorRect = offsetParent.getBoundingClientRect();
    const popupHeight = popupEl.getBoundingClientRect().height;
    if (popupHeight <= 0) {
      // 팝업 직후 콘텐츠 렌더링이 아직 끝나지 않아 높이가 0으로 측정될 수 있다.
      // 더블 rAF만으로 부족한 경우의 안전장치로 짧게 재시도하되,
      // MAX_PLACEMENT_RETRIES로 무한 루프를 방지한다.
      if (this.placementRetries < LinkPopupComponent.MAX_PLACEMENT_RETRIES) {
        this.placementRetries++;
        this.placementRetryTimeoutId = setTimeout(() => {
          this.placementRetryTimeoutId = undefined;
          if (this.destroyed) {
            return;
          }
          this.queueViewportPlacement();
        }, 50);
      }
      else {
        // 한도 도달: 기본 아래 배치 그대로 노출하고, 카운터를 리셋해
        // 이후 변경 감지로 재트리거되면 다시 시도할 수 있게 한다.
        console.warn(
          `LinkPopupComponent: 팝업 높이가 ${LinkPopupComponent.MAX_PLACEMENT_RETRIES}회 재시도 후에도 0 — 기본 아래 배치 유지`,
          {popupHeight},
        );
        this.placementRetries = 0;
        this.setPopupVisibility('visible');
      }
      return;
    }
    this.placementRetries = 0;

    const placement = resolveLinkPopupAutoPlacement({
      anchorHeight,
      anchorRect,
      popupHeight,
      viewportHeight: window.innerHeight,
      anchorBorderTopWidth: this.getBorderTopWidth(offsetParent),
    });

    this.applyPopupPlacement(popupEl, placement);
    this.setPopupVisibility('visible');
  }

  private setPopupVisibility(visibility: 'visible' | 'hidden'): void {
    this.styleVisibility = visibility;
    // @HostBinding('style.visibility') 단일 경로로 적용. setPopupTop과 동일하게
    // rAF 콜백에서 호출되더라도 detectChanges로 즉시 반영한다.
    this.cdRef.detectChanges();
  }

  private applyPopupPlacement(popupEl: HTMLElement, placement: LinkPopupPlacementStyle): void {
    this.setPopupTop(placement.top);
    if (placement.kind === 'constrained') {
      this.renderer.setStyle(popupEl, 'max-height', `${Math.floor(placement.maxHeight)}px`);
      this.renderer.setStyle(popupEl, 'overflow-y', placement.overflowY);
    }
    else {
      // 직전 'constrained' 호출이 남긴 inline max-height/overflow-y 만 제거한다.
      // (CSS에서 작성한 동일 속성은 inline 제거에 영향 받지 않음 — Renderer2.removeStyle은
      // 인라인 스타일만 지운다.)
      this.renderer.removeStyle(popupEl, 'max-height');
      this.renderer.removeStyle(popupEl, 'overflow-y');
    }
  }

  private getBorderTopWidth(el: HTMLElement): number {
    return parseFloat(getComputedStyle(el).borderTopWidth) || 0;
  }

}
